/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { OperatorDetailsDto } from '../api/operators/operatorsClient.typings';
import { OperatorDetailsActions, OperatorDetailsActionType } from '../actions/operatorDetailsActons';
import {
    CashRegisterModelsListDto,
    OperatorCashRegistersListFilterParams,
} from '../api/cashRegisters/cashRegistersClient.typings';
import { Order, PageQueryParams, SortQueryParams } from '../api/apiCommon.typings';
import { CashRegistersListTableData } from '../../components/RegistryWidgets/CashRegisters/CashRegistersList/CashRegistersListConfig';
import { OperatorCashBoxesStats } from '../api/cashBoxes/cashBoxesClient.typings';

export const rowsPerPage = [20, 30, 40];
export const defaultPaginationLimit = rowsPerPage[0];
export const defaultSortingKey: keyof CashRegistersListTableData = 'name';
export const defaultSortingOrder: Order = 'asc';

export interface OperatorDetailsState {
    operatorTaxNumber: string;
    operatorDetails: OperatorDetailsDto;
    operatorCashBoxesStats: OperatorCashBoxesStats;
    operatorCashRegisterModelsList: CashRegisterModelsListDto;
    modelsPageParams: PageQueryParams;
    modelsSortParams: SortQueryParams;
    modelsFilterParams: OperatorCashRegistersListFilterParams;
    isLoading: boolean;
    isLoadingCashBoxesStats: boolean;
    isLoadingCashRegisters: boolean;
}

const initialState: OperatorDetailsState = {
    operatorTaxNumber: null,
    operatorDetails: null,
    operatorCashBoxesStats: null,
    operatorCashRegisterModelsList: null,
    modelsPageParams: {
        limit: defaultPaginationLimit,
        offset: 0,
    },
    modelsFilterParams: {
        name: '', //search text
        status: null,
    },
    modelsSortParams: {
        sort: `${defaultSortingKey},${defaultSortingOrder}`,
    },
    isLoading: false,
    isLoadingCashBoxesStats: false,
    isLoadingCashRegisters: false,
};

function operatorDetailsReducer(
    state: OperatorDetailsState = initialState,
    action: OperatorDetailsActions,
): OperatorDetailsState {
    switch (action.type) {
        case OperatorDetailsActionType.OPEN_REGISTRY_OPERATOR_CARD_PAGE:
            const previousTaxNumber = state.operatorTaxNumber;
            const currentTaxNumber = action.payload;

            let isNewCardOpen = true;
            if (previousTaxNumber && currentTaxNumber) isNewCardOpen = previousTaxNumber !== currentTaxNumber;

            return {
                ...state,
                isLoading: true,
                isLoadingCashBoxesStats: true,
                isLoadingCashRegisters: true,
                operatorTaxNumber: action.payload,
                modelsPageParams: isNewCardOpen ? initialState.modelsPageParams : { ...state.modelsPageParams },
                modelsSortParams: isNewCardOpen ? initialState.modelsSortParams : { ...state.modelsSortParams },
                modelsFilterParams: isNewCardOpen ? initialState.modelsFilterParams : { ...state.modelsFilterParams },
            };

        case OperatorDetailsActionType.APPLY_CASH_REGISTERS_FILTER:
            return {
                ...state,
                isLoadingCashRegisters: true,
                modelsFilterParams: action.payload,
                modelsPageParams: { ...state.modelsPageParams, offset: 0 },
            };

        case OperatorDetailsActionType.APPLY_CASH_REGISTERS_PAGING:
            return {
                ...state,
                isLoadingCashRegisters: true,
                modelsPageParams: action.payload,
            };

        case OperatorDetailsActionType.APPLY_CASH_REGISTERS_SORTING:
            return {
                ...state,
                isLoadingCashRegisters: true,
                modelsSortParams: action.payload,
            };

        case OperatorDetailsActionType.FETCH_OPERATOR_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                operatorDetails: action.payload,
            };

        case OperatorDetailsActionType.FETCH_OPERATOR_CASHBOXES_STATS_SUCCESS:
            return {
                ...state,
                isLoadingCashBoxesStats: false,
                operatorCashBoxesStats: action.payload,
            };

        case OperatorDetailsActionType.FETCH_OPERATOR_CASH_REGISTERS_SUCCESS:
            return {
                ...state,
                isLoadingCashRegisters: false,
                operatorCashRegisterModelsList: action.payload,
            };

        case OperatorDetailsActionType.FETCH_OPERATOR_CARD_DATA_ERROR:
            return {
                ...state,
                isLoading: false,
                isLoadingCashBoxesStats: false,
                isLoadingCashRegisters: false,
                operatorCashRegisterModelsList: {
                    count: 0,
                    cashRegisterModels: [],
                },
            };

        case OperatorDetailsActionType.EXIT_REGISTRY_OPERATOR_CARD_PAGE:
            return {
                ...state,
                operatorDetails: null,
                operatorCashBoxesStats: null,
                operatorCashRegisterModelsList: null,
            };

        default:
            return state;
    }
}

export default operatorDetailsReducer;
